import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useClock } from "@/hooks/useClock";
import { currentUser, logout } from "@/lib/auth";
import { LiveIndicator } from "./LiveIndicator";
import type { WSState } from "@/lib/ws";

interface Props {
  wsState: WSState;
}

const pageTitles: Record<string, { title: string; subtitle: string }> = {
  "/": { title: "Dashboard", subtitle: "Sync-Status TCM → Moodle / Entra" },
  "/settings": { title: "Einstellungen", subtitle: "Lieferarten & Willkommens-Mail" },
  "/logs": { title: "Logs", subtitle: "Live-Server-Logs" },
};

/**
 * Top-Header. Zeigt den Page-Titel links (kontextabhängig vom Route),
 * Live-Indicator + Uhrzeit rechts.
 */
export function Header({ wsState }: Props) {
  const { pathname } = useLocation();
  const now = useClock();
  const hh = now.toISOString().slice(11, 19);
  const meta = pageTitles[pathname] ?? {
    title: "TCM Business Service",
    subtitle: "Operator Console",
  };

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur-md">
      <div className="flex h-14 items-center justify-between gap-6 px-6">
        <div className="flex flex-col leading-tight min-w-0">
          <span className="font-serif text-base tracking-tight text-foreground truncate">
            {meta.title}
          </span>
          <span className="eyebrow">{meta.subtitle}</span>
        </div>

        <div className="flex items-center gap-6 shrink-0">
          <LiveIndicator state={wsState} />
          <div className="hidden h-6 w-px bg-border md:block" />
          <div className="hidden items-baseline gap-2 md:flex">
            <span className="eyebrow">UTC</span>
            <time className="font-mono text-sm tabular-nums text-foreground/90">
              {hh}
            </time>
          </div>
          <UserMenu />
        </div>
      </div>
      <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
    </header>
  );
}

/**
 * UserMenu — Avatar mit Initialen; Klick öffnet ein kleines Dropdown mit
 * Name/UPN und Abmelden. Erscheint nur bei aktivem OIDC-Login.
 */
function UserMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const user = currentUser();

  // Klick außerhalb schließt das Menü.
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!user) return null;

  const initials = (user.name || user.upn)
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={`Angemeldet als ${user.name}`}
        title={user.name}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-signal-live/15 ring-1 ring-signal-live/40 font-mono text-xs font-semibold text-signal-live hover:ring-signal-live/70 transition-shadow"
      >
        {initials}
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-50 w-64 rounded-md border border-border bg-background shadow-lg">
          <div className="border-b border-border px-4 py-3">
            <div className="truncate text-sm font-medium text-foreground">{user.name}</div>
            <div className="truncate text-xs text-muted-foreground" title={user.upn}>
              {user.upn}
            </div>
          </div>
          <button
            onClick={() => void logout()}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Abmelden
          </button>
        </div>
      )}
    </div>
  );
}
